package com.neurobs.ccab

import com.neurobs.ccab.SessionConfig.{ParameterRestriction, StudyConfig}
import com.neurobs.util.{Guid, SecondsTimestamp}
import io.circe.{Decoder, Encoder}
import com.neurobs.errors._

import java.time.{ZoneId, ZoneOffset}
import java.time.temporal.ChronoUnit
import scala.concurrent.Future

case class SessionConfig
(
	version: String,
	default_protocol_id: Guid,
	sequence_names: Seq[SessionConfig.SequenceName],
	parameters: Seq[SessionConfig.Parameter],
	tests: Seq[SessionConfig.Test],
	protocols: Seq[SessionConfig.Protocol]
) {
	def checkRestriction(r: ParameterRestriction): Result[Unit] = {
		parameters.find(_.identifier == r.identifier) match {
			case None => Left(s"The parameter '${r.identifier}' was not found.")
			case Some(p) =>
				if (r.values.toSet.subsetOf(p.values.toSet)) Right(Unit)
				else Left(s"The parameter values '${r.values}' are not valid for the parameter '${r.identifier}'.")
		}
	}

	def defaultStudyConfig: StudyConfig = StudyConfig(protocol = default_protocol_id, parameter_restrictions = None)
}

trait SessionConfigProvider
{
	def get(): Future[Result[SessionConfig]]
}

object SessionConfig {

	object TaskType extends Enumeration {
		val Intro = Value("intro")
		val Break = Value("break")
		val Speech = Value("speech")
		val NonSpeech = Value("nonspeech")
	}

	case class Test
	(
		identifier: String,
		display_name: String,
		task_type: TaskType.Value
	)

	case class Parameter
	(
		identifier: String,
		display_name: String,
		data_type: String,
		values: Seq[String]
	)

	case class SequenceName
	(
		group_name: String,
		sequence_name: String
	)

	case class ParameterRestriction
	(
		identifier: String,
		values: Seq[String]
	) {
		def intersect(other: ParameterRestriction): ParameterRestriction = {
			val commonValues = values.intersect(other.values)
			ParameterRestriction(identifier, commonValues)
		}
	}

	object TimeType extends Enumeration {
		val Absolute = Value("absolute")
		val Relative = Value("relative")
	}

	object DurationUnit extends Enumeration {
		val Day = Value("day")
		val Month = Value("month")
		val Year = Value("year")
	}

	case class Duration
	(
		value: Double,
		unit: DurationUnit.Value
	)

	case class Session
	(
		id: Guid,
		label: String,
		display_name: String,
		time: Option[Duration],
		time_type: Option[TimeType.Value],
		max_time: Option[Duration],
		required: Option[Boolean],
		group_name: String,
		sequence_name: String,
		legacy_sequence_names: Option[Seq[String]]
	) {
		def getTime: Duration = time.getOrElse(Duration(value = 0, unit = DurationUnit.Day))
		def getTimeType: TimeType.Value = time_type.getOrElse(TimeType.Absolute)
		def getRequired: Boolean = required.getOrElse(true)
		def description: String = s"$display_name ($label)"
	}

	case class Protocol
	(
		id: Guid,
		display_name: String,
		parameter_restrictions: Option[Seq[ParameterRestriction]],
		sessions: Seq[Session]
	)

	case class StudyConfig
	(
		protocol: Guid,
		parameter_restrictions: Option[Seq[ParameterRestriction]]
	)

	implicit val taskTypeDecoder: Decoder[TaskType.Value] = Decoder.decodeEnumeration(TaskType)
	implicit val timeTypeDecoder: Decoder[TimeType.Value] = Decoder.decodeEnumeration(TimeType)
	implicit val timeTypeEncoder: Encoder[TimeType.Value] = Encoder.encodeEnumeration(TimeType)
	implicit val durationDecoder: Decoder[Duration] = Decoder.decodeString.emap { str =>
		val pattern = """(\d+)([dmy])""".r
		str match {
			case pattern(value, unit) =>
				val durationUnit = unit match {
					case "d" => Right(DurationUnit.Day)
					case "m" => Right(DurationUnit.Month)
					case "y" => Right(DurationUnit.Year)
					case _   => Left("Invalid duration unit")
				}
				durationUnit.map(Duration(value.toInt, _))
			case _ => Left("Invalid duration format")
		}
	}

	def addDuration(timestamp: SecondsTimestamp, duration: Duration): SecondsTimestamp = {
		val instant = timestamp.instant
		// Do not use ZonedDateTime because the Scala.js implementation requires an additional
		// timezone database dependency
		val offsetDateTime = instant.atOffset(ZoneOffset.ofHours(0))
		//val zonedDateTime = instant.atZone(ZoneId.systemDefault())
		val updatedDateTime = duration.unit match {
			case DurationUnit.Day   => offsetDateTime.plus(duration.value.toLong, ChronoUnit.DAYS)
			case DurationUnit.Month => offsetDateTime.plus(duration.value.toLong, ChronoUnit.MONTHS)
			case DurationUnit.Year  => offsetDateTime.plus(duration.value.toLong, ChronoUnit.YEARS)
		}
		SecondsTimestamp(updatedDateTime.toInstant)
	}
}
